import { PALETTE } from '@overlayqa/shared';
import { Link } from 'react-router-dom';
import type { DashboardActivityItem } from '@overlayqa/shared';
import { HugeiconsIcon } from '@hugeicons/react';
import { Clock01Icon } from '@hugeicons/core-free-icons';
import { chipBaseClasses } from '../shared/badges/badgeBase';
import { timeAgo } from '../../lib/utils';

interface RecentActivitySectionProps {
  items: DashboardActivityItem[];
  variant?: 'default' | 'overview';
}

const ACTIVITY_CONFIG: Record<string, { label: string; bg: string; text: string }> = {
  issue_created: { label: 'Created', bg: 'bg-violet-50', text: 'text-violet-600' },
  status_in_progress: { label: 'In Progress', bg: 'bg-amber-50', text: 'text-amber-600' },
  status_resolved: { label: 'Resolved', bg: 'bg-[var(--color-success-bg)]', text: 'text-[var(--color-success)]' },
  status_closed: { label: 'Closed', bg: 'bg-gray-100', text: 'text-gray-500' },
  issue_updated: { label: 'Updated', bg: 'bg-gray-100', text: 'text-gray-500' },
};

export function RecentActivitySection({ items, variant = 'default' }: RecentActivitySectionProps) {
  const overview = variant === 'overview';
  const filtered = items.filter((item) => item.type !== 'comment').slice(0, 6);

  return (
    <section id="recent-activity" aria-label="Recent activity" className={overview ? 'min-w-0 flex flex-col' : 'card-sm min-w-0 flex flex-col'}>
      <h2 className="section-label mb-3 flex items-center gap-2">
        <HugeiconsIcon icon={Clock01Icon} size={16} strokeWidth={2} color="currentColor" className="w-4 h-4 text-gray-400" aria-hidden="true" />
        Recent Activity
      </h2>
      <div className={overview ? 'card-sm min-w-0 flex-1' : 'flex-1'}>
        {overview && <p className="mb-5 text-xs text-gray-500">Latest changes across your projects</p>}
        {filtered.length === 0 ? (
          <div className="empty-state-compact flex flex-col items-center justify-center gap-2 h-full">
            <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center mb-1">
              <svg className="w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v6l4 2m6-2a10 10 0 11-20 0 10 10 0 0120 0z" />
              </svg>
            </div>
            No recent activity
          </div>
        ) : (
          <div>
            <ol className="relative">
              {filtered.map((item, idx) => {
                const isLast = idx === filtered.length - 1;
                return (
                  <li key={`${item.type}-${item.id}`} className="relative pl-5 pb-4 last:pb-0">
                    {/* Vertical line: hairline, not brand-saturated */}
                    {!isLast && (
                      <div
                        className="absolute left-[3.5px] top-[18px] w-px bg-gray-100"
                        style={{ bottom: 0 }}
                        aria-hidden="true"
                      />
                    )}
                    {/* Dot: brand only marks the newest event; the rest are
                        quiet so the eye lands on what's new, not the whole rail. */}
                    <div
                      className="absolute left-0 top-[10px] w-2 h-2 rounded-full ring-2 ring-white"
                      style={{ backgroundColor: idx === 0 ? 'var(--color-primary)' : PALETTE.neutral[300] }}
                      aria-hidden="true"
                    />
                    <Link
                      to={`/issues/${item.displayId ?? item.id}`}
                      className="block group -mx-2 px-2 py-1 rounded-md hover:bg-gray-50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500"
                      title={overview ? undefined : item.title}
                    >
                      {overview ? <>
                        <p className="text-sm font-medium text-gray-700 line-clamp-2 break-words">{item.title}</p>
                        <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                          <span className="shrink-0">{(ACTIVITY_CONFIG[item.type] ?? ACTIVITY_CONFIG.issue_updated).label}</span>
                          <span aria-hidden="true">·</span>
                          <span className="min-w-0 flex-1 truncate">{item.projectName}</span>
                          <time dateTime={item.timestamp} className="shrink-0">{timeAgo(item.timestamp)}</time>
                        </div>
                      </> : <>
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-xs font-medium text-gray-900 group-hover:text-primary-600 transition-colors leading-snug truncate">
                          {item.title}
                        </p>
                        {/* Artifact activity row shows only the id here (no
                            severity badge); severity lives in Needs Attention. */}
                        <span className="text-[10px] font-mono text-gray-400 shrink-0">{item.displayId ?? `#${item.id.slice(0, 8)}`}</span>
                      </div>
                      <div className="flex items-center justify-between gap-2 mt-1">
                        {(() => {
                          const config = ACTIVITY_CONFIG[item.type] ?? ACTIVITY_CONFIG.issue_updated;
                          return (
                            <span className={`${chipBaseClasses} ${config.bg} ${config.text}`}>
                              {config.label}
                            </span>
                          );
                        })()}
                        <time className="text-xs text-gray-400 shrink-0">{timeAgo(item.timestamp)}</time>
                      </div>
                      </>}
                    </Link>
                  </li>
                );
              })}
            </ol>
          </div>
        )}
      </div>
    </section>
  );
}

export function RecentActivitySkeleton({ variant = 'default' }: { variant?: 'default' | 'overview' }) {
  if (variant === 'overview') return <section aria-label="Recent activity loading" className="min-w-0 flex flex-col">
    <div className="h-4 w-32 mb-3 rounded skeleton-shimmer" />
    <div className="card-sm">
    <div className="h-2.5 w-44 mt-3 mb-6 rounded skeleton-shimmer" />
    <div className="space-y-5">{[1, 2, 3, 4].map(row => <div key={row} className="pl-5 space-y-2"><div className="h-3 w-3/4 rounded skeleton-shimmer" /><div className="h-2.5 w-full rounded skeleton-shimmer" /></div>)}</div>
    </div>
  </section>;
  return (
    <section aria-label="Recent activity loading">
      <div className="skeleton-heading w-32" />
      <div className="list-container px-5 py-3">
        <div className="relative">
          {[1, 2, 3, 4].map((i) => {
            const skelOpacity = 1 - ((i - 1) / 3) * 0.75;
            const skelLineOpacity = 1 - ((i - 0.5) / 3) * 0.75;
            return (
            <div key={i} className="relative pl-5 pb-4 last:pb-0">
              <div className="absolute left-0 top-[10px] w-2 h-2 rounded-full" style={{ backgroundColor: `rgba(var(--color-primary-rgb), ${skelOpacity * 0.3})` }} />
              {i < 4 && <div className="absolute left-[3.5px] top-[18px] w-px" style={{ bottom: 0, backgroundColor: `rgba(var(--color-primary-rgb), ${skelLineOpacity * 0.3})` }} />}
              <div className="skeleton-line w-24 mb-2" />
              <div className="skeleton-line w-3/4" />
            </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
